import React, { useState, useEffect } from "react";
import { getTweetReachData } from "../../../services/dashboard";
import { reach as texts_es } from "../../../utils/text/graphTexts";
import { reach as texts_en } from "../../../utils/text/graphTexts_en";
import { generateMultiplLineTooltip } from "../../../utils/functions/generateLineTooltip";
import { useParams } from "react-router-dom";
import { COUNTRY_OPTIONS } from "../../../utils/constants/countryOptions";
import CardContainer from "../../reusable/CardContainer";
import CenterSpin from "../../misc/spin";
import CardOnlyYear from "../../reusable/CardOnlyYear";
import { getDateString } from "../../../utils/functions/getDateString";
import { getMonthlyTweetReach } from "../../../services/singleMonths";
import {
  getTwoLineGraphOptions,
  GRID_CONFIG_WITH_LEGEND,
  LEGEND_CONFIG,
  TOOLTIP_STYLE,
} from "../../../utils/constants/graphConfigs";
import { makeSeries } from "../../../utils/functions/makeSeries";
import { handleYearChange } from "../../../utils/functions/handleYearChange";
import { getMonthText } from "../../../utils/functions/getMonthText";
import { useContextData } from "../../../hooks/context";
import { useCurrentLanguage } from "../../../hooks/useCurrentLanguage";
import CardError from "../../reusable/CardError/CardError";
import miscText from "../../../utils/text/misc.json";
import { getGraphFileName } from "../../../utils/functions/getGraphFileName";
import { getDownloadSubtitle } from "../../../utils/functions/getDownloadSubtitle";

export default function Reach({ selectedCountry, colorRBG }) {
  const { country } = useParams();
  const { dateOptions } = useContextData();
  const { language } = useCurrentLanguage();

  // State

  const [currentCountry, setCurrentCountry] = useState(
    selectedCountry ?? country
  );
  const [currentCardType] = useState("reach");
  const [texts, setTexts] = useState(language === "en" ? texts_en : texts_es);
  const [countryProps, setCountryProps] = useState(
    COUNTRY_OPTIONS.find(
      (opt) => opt.code === selectedCountry || opt.code === country
    )
  );
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorLoadingGraph, setErrorLoadingGraph] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [options, setOptions] = useState();
  const [selectedYear, setSelectedYear] = useState();
  const [isMostRecentYear, setIsMostRecentYear] = useState(true);

  // Unique to Tweets component
  // Never send unique states to Card components
  const [currentYearPosSum, setCurrentYearSum] = useState();
  const [currentYearXenoSum, setCurrentYearXenoSum] = useState(true);

  const createDateString = () => {
    let dateString;

    try {
      dateString = isMostRecentYear
        ? getDateString(
            language,
            dateOptions.mostRecentDate.year,
            dateOptions.mostRecentDate.month
          )
        : getDateString(
            language,
            selectedYear === "HIST"
              ? dateOptions.mostRecentDate.year
              : selectedYear
          );
    } catch (error) {
      dateString = "-----";
    }

    return dateString;
  };

  // Useeffect that collents yead and month data
  useEffect(() => {
    if (dateOptions) {
      setSelectedYear("HIST");
      setIsLoadingInitialData(false);
      setCountryProps(
        COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions]);

  // useEffect to load data for graph and creates Echarts options
  // Listend to dateOptions and selected year
  // Only funs when these params are not null
  useEffect(() => {
    async function updateData() {
      try {
        setIsLoading(true);

        const response = await getTweetReachData(currentCountry, selectedYear);

        if (response) {
          const monthsRealNames = response.data.dates.map((date) =>
            getMonthText(date, language, false)
          );

          const options = {
            ...getTwoLineGraphOptions(
              monthsRealNames,
              null,
              null,
              colorRBG,
              language
            ),
            legend: {
              ...LEGEND_CONFIG,
              data:
                language === "en"
                  ? ["Positive", "Xenophobic"]
                  : ["Positivos", "Xenófobos"],
            },
            tooltip: {
              trigger: "axis",
              ...TOOLTIP_STYLE,
              formatter: function (params) {
                const transformedNames =
                  language === "en"
                    ? {
                        Positive: response.data.data.positive,
                        Xenophobic: response.data.data.xeno,
                      }
                    : {
                        Positivos: response.data.data.positive,
                        Xenófobos: response.data.data.xeno,
                      };
                const isPercentage = false;
                const areColorsSwitched = false;
                return generateMultiplLineTooltip(
                  params,
                  transformedNames,
                  isPercentage,
                  areColorsSwitched,
                  language
                );
              },
            },
            grid: GRID_CONFIG_WITH_LEGEND,
            series: makeSeries(response.data, language, true),
          };

          setOptions(options);
          setIsDataAvailable(true);
          setIsLoading(false);
          setErrorLoadingGraph(false);
        }
      } catch (error) {
        setErrorLoadingGraph(true);
        setIsDataAvailable(false);
        setOptions(null);
        setIsLoading(false);
      }
    }

    if (!isLoadingInitialData && dateOptions && selectedYear) updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions, selectedYear, isLoadingInitialData, currentCountry]);

  // useEffect to load data for description dynamic data
  useEffect(() => {
    const getData = async () => {
      let promises, callback;

      if (isMostRecentYear) {
        const currentMonthPromise = getMonthlyTweetReach(
          currentCountry,
          dateOptions.mostRecentDate.year,
          dateOptions.mostRecentDate.month
        );

        promises = [currentMonthPromise];
        callback = (results) => {
          setCurrentYearSum(results[0].data.positive_reach);
          setCurrentYearXenoSum(results[0].data.xeno_reach);
        };
      } else {
        const currentYearPromise =
          selectedYear === "HIST"
            ? getTweetReachData(currentCountry, dateOptions.mostRecentDate.year)
            : getTweetReachData(currentCountry, selectedYear);

        promises = [currentYearPromise];
        callback = (results) => {
          const posSum = results[0].data.data.positive.reduce(
            (a, b) => a + b,
            0
          );
          const xenoSum = results[0].data.data.xeno.reduce((a, b) => a + b, 0);

          setCurrentYearSum(posSum);
          setCurrentYearXenoSum(xenoSum);
        };
      }
      Promise.all(promises)
        .then(callback)
        .catch((error) => {
          setCurrentYearSum("--");
          setCurrentYearXenoSum("--");
        });
    };

    if (!isLoadingInitialData && dateOptions) getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions, isLoadingInitialData, selectedYear, currentCountry]);

  useEffect(() => {
    if (selectedCountry) {
      setCurrentCountry(selectedCountry);
      setCountryProps(
        COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry)
      );
    }
  }, [selectedCountry]);

  useEffect(() => {
    setTexts(language === "en" ? texts_en : texts_es);
  }, [language]);

  function ReachContent() {
    let content;
    try {
      content = (
        <CardOnlyYear
          countryProps={countryProps}
          dateOptions={dateOptions}
          isLoading={isLoading}
          errorLoading={errorLoadingGraph}
          isDataAvailable={isDataAvailable}
          options={options}
          selectedYear={selectedYear}
          lastMonthData={currentYearPosSum}
          dataDate={createDateString()}
          cardTitle={texts.boxTitle}
          methodology={texts.methodology}
          dataSubtitle={texts.dataSubtitle}
          description={texts.description(
            isMostRecentYear,
            isMostRecentYear ? dateOptions.mostRecentDate.month : selectedYear,
            currentYearPosSum,
            currentYearXenoSum
          )}
          selectArray={[
            {
              selectedValue: selectedYear,
              changeHandler: (newSelectedYear) =>
                handleYearChange(
                  newSelectedYear,
                  setIsMostRecentYear,
                  setSelectedYear,
                  dateOptions
                ),
              options: dateOptions.yearOptions,
              placeHolderText: miscText["yearPlaceholder"][language],
              labelAttributeName: "years_list",
              valueAttributeName: "years_code",
            },
          ]}
          language={language}
          downloadableCardID={currentCardType}
          downloadableCardFileName={getGraphFileName(
            language === "es" ? "AlcanceDeTweets" : "TweetReach",
            currentCountry,
            selectedYear
          )}
          downloadableCardTitle={texts.boxTitle}
          downloadableCardSubtitle={getDownloadSubtitle(
            countryProps,
            selectedYear,
            language
          )}
          downloadableCardContainsLegend={true}
        />
      );
    } catch (error) {
      content = (
        <CardError
          cardTitle={texts.boxTitle}
          methodology={texts.methodolog}
          dataSubtitle={texts.dataSubtitle}
          description={texts.description()}
          selectArray={[
            {
              placeHolderText: miscText["yearPlaceholder"][language],
              language: language,
            },
          ]}
        />
      );
    }

    return content;
  }

  return (
    <CardContainer>
      {isLoadingInitialData ? <CenterSpin /> : <ReachContent />}
    </CardContainer>
  );
}
